import {
  Alert,
  AlertIcon,
  Box,
  Center,
  CloseButton,
  Heading,
  Text,
  Spinner,
  Button,
  Stack,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { useState } from "react";
import { BsCalendarWeek } from "react-icons/bs";
import { Link, useParams } from "react-router-dom";
import BackButton from "../../components/BackButton";
import BookingForm from "../../components/booking/BookingForm";
import SpaceSlideShow from "../../components/booking/SpaceSlideShow";
import BottomSheetWrapper from "../../components/BottomSheetWrapper";
import FavouriteButton from "../../components/FavouriteButton";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import { usePoi } from "../../data/mazemap.api";
import {
  BookingRequest,
  useBookDeskMutation,
  useBookingLocation,
  useValidateBooking,
} from "../../data/occupancy.api";
import { useFocusOnFeature } from "../../hooks/useFocusOnFeature";

const BookDesk = () => {
  const { plannonId, deskId } = useParams<{
    plannonId: string;
    deskId: string;
  }>();
  // console.log(plannonId, deskId);
  const { data, isLoading, isError } = useBookingLocation(plannonId);
  const { data: poi } = usePoi(data?.poiId);
  const [bookingRequest, setBookingRequest] = useState<BookingRequest>();
  const book = useBookDeskMutation(plannonId, bookingRequest);
  const {
    isLoading: validating,
    isError: validationFailed,
    error: validationError,
  } = useValidateBooking(bookingRequest);

  useFocusOnFeature(poi, 17.7);

  return (
    <HybridBottomSheetDrawer>
      <BackButton />
      {isLoading ? (
        <Center p={8}>
          <Spinner />
        </Center>
      ) : isError ? (
        <Center p={8}>
          <Alert status="error">
            <AlertIcon />
            Could not find a location with ID {plannonId}
          </Alert>
        </Center>
      ) : data && data.availableDesks < 1 ? (
        <Center p={8}>
          <Alert status="warning">
            <AlertIcon />
            This location is not bookable
          </Alert>
        </Center>
      ) : data ? (
        <>
          <SpaceSlideShow images={data.images ?? []} />

          <Box position="relative" p={8}>
            <Flex mb={5} justify="space-between">
              <Box>
                <Heading size="md">{data.name}</Heading>
                {data.availableDesks === 1 ? (
                  <Text opacity={0.7}>{data.availableDesks} space</Text>
                ) : (
                  <Text opacity={0.7}>{data.availableDesks} spaces</Text>
                )}
              </Box>
              <FavouriteButton name={data.name} />
            </Flex>

            {book.isIdle && validationError instanceof Error ? (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {validationError.message}
              </Alert>
            ) : null}

            {book.isSuccess ? (
              <Alert status="success">
                <AlertIcon />
                Success! You should receive an email shortly confirming your
                desk booking.
              </Alert>
            ) : book.isError ? (
              <Alert status="error">
                <AlertIcon />
                {book.error instanceof Error ? book.error.message : null}
              </Alert>
            ) : (
              <BookingForm
                deskId={deskId}
                error={validationError instanceof Error}
                bookingLocation={data}
                onChange={setBookingRequest}
              />
            )}

            <Box mt={6}>
              {book.isError || book.isSuccess ? (
                <Flex gap={2}>
                  <Button as={Link} to="/" w="full">
                    Go Home
                  </Button>
                  <Button w="full" onClick={() => book.reset()}>
                    Book Again
                  </Button>
                </Flex>
              ) : (
                <Flex gap={2}>
                  <Button
                    w="full"
                    colorScheme="green"
                    isDisabled={!bookingRequest || validationFailed}
                    isLoading={validating || book.isLoading}
                    onClick={() => book.mutateAsync()}
                  >
                    {validating ? "Validating" : "Book"}
                  </Button>
                </Flex>
              )}
            </Box>
          </Box>
        </>
      ) : null}
    </HybridBottomSheetDrawer>
  );
};

export default BookDesk;
